import React, { PureComponent, Fragment } from 'react';
import { string, number, shape, func, bool, arrayOf } from 'prop-types';
import compose from 'lodash/flowRight';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { Button } from 'components';
import Slider from 'components/Slider/Slider';
import { connectRange } from 'react-instantsearch-core';
import { withSlider } from 'connectors';
import { intlShape } from 'shapes';
import FilterCollapsePanel from '../FilterCollapsePanel/FilterCollapsePanel';
import './RangeFilter.scss';

const messages = defineMessages({
    submitLabel: {
        id: 'listing.filters.range.submitButtonLabel',
        defaultMessage: 'Show products',
        description: 'Label of the button to apply range filter',
    },
});

class RangeFilter extends PureComponent {
    static propTypes = {
        min: number,
        max: number,
        id: string,
        label: string.isRequired,
        attribute: string,
        currentRefinement: shape({
            min: number,
            max: number,
        }).isRequired,
        count: arrayOf(
            shape({
                count: number.isRequired,
                value: string.isRequired,
            }),
        ).isRequired,
        refine: func.isRequired,
        className: string,
        openedByDefault: bool,
        canShowNoResultLabel: bool,
        onFilterSelected: func.isRequired,
        slider: shape({
            createSlider: func,
        }).isRequired,
        intl: intlShape.isRequired,
    };

    static defaultProps = {
        className: null,
        attribute: '',
        id: '',
        min: null,
        max: null,
        openedByDefault: true,
        canShowNoResultLabel: true,
    };

    static getDerivedStateFromProps(props, state) {
        let newState = { changedByUser: false };
        const { currentRefinement } = props;
        const { valueMin, valueMax, changedByUser } = state;

        if (
            (currentRefinement.min !== valueMin ||
                currentRefinement.max !== valueMax) &&
            !changedByUser
        ) {
            newState = {
                ...newState,
                valueMin: currentRefinement.min,
                valueMax: currentRefinement.max,
            };
        }

        // if min or max are changed by algolia check if the values of the sliders entered by the user for corresponding to the new bounds
        if (props.min !== state.min) {
            newState.min = props.min;
            if (valueMin < props.min) {
                newState.valueMin = props.min;
            }
        }
        if (props.max !== state.max) {
            newState.max = props.max;
            if (valueMax > props.max) {
                newState.valueMax = props.max;
            }
        }

        if (
            props.min !== null &&
            props.max !== null &&
            currentRefinement.min !== undefined &&
            currentRefinement.max !== undefined
        ) {
            newState.canInitializeSlider = true;
        } else {
            newState.canInitializeSlider = false;
        }

        return newState;
    }

    constructor(props) {
        super(props);

        this.state = {
            valueMin: props.currentRefinement.min || null,
            valueMax: props.currentRefinement.max || null,
            min: props.min,
            max: props.max,
            changedByUser: false,
            canInitializeSlider: false,
        };
    }

    onSliderChange = (values, validateRange = false) => {
        const { min, max } = this.props;
        const nextState = {};
        if (!validateRange || values.min < max) {
            nextState.valueMin = values.min;
        }

        if (!validateRange || values.max > min) {
            nextState.valueMax = values.max;
        }

        if (Object.keys(nextState).length > 0) {
            this.setState({
                ...nextState,
                changedByUser: true,
            });
        }
    };

    submit = () => {
        const { min, max } = this.props;
        const { valueMin, valueMax } = this.state;
        const refineState = {
            min: isFinite(valueMin) ? valueMin : min,
            max: isFinite(valueMax) ? valueMax : max,
        };

        this.props.refine(refineState);
        this.props.onFilterSelected({
            value: [refineState.min, refineState.max],
            filterMin: refineState.min,
            filterMax: refineState.max,
        });
    };

    findMatchingCountByMinAndMax = (min, max) =>
        this.props.count.find(
            countItem =>
                parseInt(countItem.value, 10) >= min &&
                parseInt(countItem.value, 10) <= max,
        );

    hasMatchingHits = () => {
        const {
            canShowNoResultLabel,
            currentRefinement: { min, max },
        } = this.props;
        if (canShowNoResultLabel) {
            const match = this.findMatchingCountByMinAndMax(min, max) || {};
            return match.count > 0;
        }
        return true;
    };

    render() {
        const { valueMin, valueMax, canInitializeSlider } = this.state;
        const {
            label,
            attribute,
            min,
            max,
            id,
            refine,
            className,
            openedByDefault,
            slider,
            intl,
        } = this.props;

        return (
            <FilterCollapsePanel
                label={label}
                className={className}
                dataQa={attribute}
                openedByDefault={openedByDefault}
            >
                {this.hasMatchingHits() ? (
                    slider &&
                    canInitializeSlider && (
                        <Fragment>
                            <Slider
                                valueMin={valueMin}
                                valueMax={valueMax}
                                min={min}
                                max={max}
                                id={id}
                                submit={refine}
                                onChange={this.onSliderChange}
                                slider={slider}
                            />

                            <div className="filter-slider__container-button">
                                <Button
                                    mode="discreet-primary"
                                    onClick={this.submit}
                                    label={intl.formatMessage(
                                        messages.submitLabel,
                                    )}
                                />
                            </div>
                        </Fragment>
                    )
                ) : (
                    <p className="range-filter__noResult">
                        <FormattedMessage
                            id="listing.filters.noResult"
                            defaultMessage="No result"
                            description="No result label"
                        />
                    </p>
                )}
            </FilterCollapsePanel>
        );
    }
}

export default compose(connectRange, withSlider, injectIntl)(RangeFilter);
